import React, { useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Copy,
  Check,
} from "lucide-react";
import { RiMarkPenLine, RiDeleteBinLine } from "react-icons/ri";
import { AiOutlineStop } from "react-icons/ai";
import moment from "moment";
import { useUserStore } from "../store/useUser";
import _ from "lodash";
import { useNavigate } from "react-router-dom";

interface PaginatedUserResponse {
  data: any[];
  onPageChange: (page: number) => void;
  searchTerm?: string;
  page: number;
  totalPages: number;
  isLoading: boolean;
  isBusiness?: boolean; // Flag to determine if we're showing business users
}

const OnboardingTable = ({
  data,
  onPageChange,
  searchTerm = "",
  page = 1,
  totalPages = 1,
  isLoading,
  isBusiness = false, // Default to regular users
}: PaginatedUserResponse) => {
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [suspendUser, setSuspendUser] = useState(false);
  const [activateUser, setActivateUser] = useState("");
  const setUserDetailsModal = useUserStore((state) => state.setShowUserDetails);
  const showUserDetailsModal = useUserStore((state) => state.showUserDetails);
  const setUserId = useUserStore((state) => state.setSelectedUsersId);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    let startPage = Math.max(1, page - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const handleFirstPage = () => onPageChange(1);
  const handleLastPage = () => onPageChange(totalPages);
  const handlePrevPage = () => onPageChange(Math.max(1, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages, page + 1));

  // @ts-ignore
  const filteredData = data?.data?.filter((item: any) => {
    if (isBusiness) {
      return (
        item?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.refereeCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.contactPerson?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } else {
      return (
        item?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.refereeCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.fullName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
  });

  const handleCopyCode = (code: string, userId: string) => {
    navigator.clipboard.writeText(code);
    setCopiedId(userId);

    // Reset copied status after 2 seconds
    setTimeout(() => {
      setCopiedId(null);
    }, 2000);
  };

  // Function to get status badge styling
  const getStatusBadgeStyle = (status: string | null) => {
    status = (status || "").toLowerCase();

    if (!status) return "bg-gray-100 text-gray-800"; // Default for null/empty

    switch (status) {
      case "active":
      case "approved":
      case "verified":
        return "bg-green-100 text-green-800";
      case "pending":
      case "in review":
      case "in progress":
        return "bg-yellow-100 text-yellow-800";
      case "blocked":
      case "suspended":
      case "rejected":
        return "bg-red-100 text-red-800";
      case "inactive":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  // Define columns based on user type
  const regularColumns = [
    { key: "sn", label: "S/N", align: "left" },
    { key: "createdAt", label: "Date/Time", align: "left" },
    { key: "fullName", label: "Full Name", align: "left" },
    { key: "refereeCode", label: "User's Referral Code", align: "left" },
    { key: "email", label: "Email", align: "left" },
    { key: "invitationCode", label: "Invitation Code", align: "left" },
    { key: "profileStatus", label: "Profile Status", align: "left" },
    { key: "kycstatus", label: "KYC Status", align: "left" },
    { key: "invitedBy", label: "Invited By", align: "left" },
    { key: "transactionCount", label: "Transaction Count", align: "left" },
    { key: "action", label: "Actions", align: "left" },
  ];

  const businessColumns = [
    { key: "sn", label: "S/N", align: "left" },
    { key: "date", label: "Date/Time", align: "left" },
    { key: "businessName", label: "Business Name", align: "left" },
    { key: "contactPerson", label: "Contact Person", align: "left" },
    { key: "refereeCode", label: "Referral Code", align: "left" },
    { key: "email", label: "Email", align: "left" },
    { key: "phoneNumber", label: "Phone Number", align: "left" },
    { key: "profileStatus", label: "Profile Status", align: "left" },
    { key: "kycstatus", label: "KYC Status", align: "left" },
    { key: "invitedBy", label: "Invited By", align: "left" },
    { key: "transactionCount", label: "Transaction Count", align: "left" },
    { key: "action", label: "Actions", align: "left" },
  ];

  const columns = isBusiness ? businessColumns : regularColumns;

  const handleDetails = (id: string) => {
    navigate(`/dashboard/compliance/onboarding/${id}`);
  };

  // Calculate the starting serial number based on current page
  const startIndex = (page - 1) * 10; // Assuming 10 items per page

  return (
    <div className="bg-white rounded-lg shadow-sm w-full">
      <div className="overflow-x-auto">
        <table className="appearance-none bg-white min-w-full mb-6">
          <thead className="bg-[#fff] appearance-none">
            <tr className="border-b bg-gray-50">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`font-normal text-sm text-primary py-4 whitespace-nowrap px-4 border-r border-slate-50 ${column.align === "right" ? "text-right" : "text-left"
                    }`}
                >
                  <span className="text-[#5C6F7F] font-bold text-[12px] leading-[18px]">
                    {column.label}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="mt-3 pt-3 w-full space-y-8 border-r border-slate-50">
            {(filteredData || []).map((user: any, index: number) => {
              const date = moment(user.createdAt || user.date).format("lll");
              const serialNumber = startIndex + index + 1;

              return (
                <tr
                  key={user.userId}
                  className="appearance-none my-4 border border-slate-50 even:bg-white odd:bg-slate-100"
                >
                  {/* Serial Number column */}
                  <td className="font-normal text-sm text-[#202223] py-4 px-4">
                    {serialNumber}
                  </td>
                  <td className="font-normal text-sm text-[#202223] py-4 px-4">
                    {date}
                  </td>

                  {/* Business-specific rows */}
                  {isBusiness && (
                    <>
                      <td
                        onClick={() => {
                          setUserDetailsModal(true);
                          setUserId(user.userId);
                          handleDetails(user.businessId)
                        }}
                        className="font-normal text-sm text-[#0eb683] py-4 px-4 hover:underline cursor-pointer"
                      >
                        {user.businessName}
                      </td>
                      <td className="font-normal text-sm text-[#202223] py-4 px-4">
                        {user.contactPerson}
                      </td>
                    </>
                  )}

                  {/* Regular user rows */}
                  {!isBusiness && (
                    <td
                      onClick={() => {
                        setUserDetailsModal(true);
                        setUserId(user.userId);
                      }}
                      className="font-normal text-sm text-[#0eb683] py-4 px-4 hover:underline cursor-pointer"
                    >
                      {user.fullName}
                    </td>
                  )}

                  {/* Copyable Referral Code - common for both */}
                  <td className="font-normal text-sm text-[#202223] py-4 px-4">
                    <div className="flex items-center gap-2">
                      <span>{user.refereeCode}</span>
                      <button
                        onClick={() =>
                          handleCopyCode(user.refereeCode, user.userId)
                        }
                        className="p-1 hover:bg-gray-100 rounded-full transition-all"
                        title="Copy referral code"
                      >
                        {copiedId === user.userId ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4 text-gray-500" />
                        )}
                      </button>
                    </div>
                  </td>

                  <td className="font-normal text-sm text-[#202223] py-4 px-4">
                    {user.email}
                  </td>

                  {/* Business-specific fields */}
                  {isBusiness && (
                    <td className="font-normal text-sm text-[#202223] py-4 px-4">
                      {user.phoneNumber}
                    </td>
                  )}

                  {/* Regular user specific fields */}
                  {!isBusiness && (
                    <td className="font-normal text-sm text-[#202223] py-4 px-4">
                      {user.invitationCode}
                    </td>
                  )}

                  {/* Profile Status - common for both */}
                  <td className="font-normal text-sm text-[#202223] py-4 px-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${getStatusBadgeStyle(
                        user.profileStatus
                      )}`}
                    >
                      {_.startCase(user.profileStatus) || "Not Set"}
                    </span>
                  </td>

                  {/* KYC Status - common for both */}
                  <td className="font-normal text-sm text-[#202223] py-4 px-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${getStatusBadgeStyle(
                        user.kycstatus
                      )}`}
                    >
                      {_.startCase(user.kycstatus) || "Not Started"}
                    </span>
                  </td>

                  {/* Common fields for both */}
                  <td className="font-normal text-sm text-[#202223] py-4 px-4">
                    {user.invitedBy || "N/A"}
                  </td>
                  <td className="font-normal text-sm text-[#202223] py-4 px-4">
                    {user.transactionCount}
                  </td>
                  <td className="font-normal text-sm text-[#202223] py-4 px-4">
                    <div className="flex items-center gap-1 py-2">
                      <span
                        onClick={() => handleDetails(user.businessId)}
                        className="hover:underline p-1 text-[#0eb683] cursor-pointer transition-all active:scale-90"
                        title="Edit"
                      >
                        View More
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <div className="flex items-center gap-2">
            <button
              onClick={handleFirstPage}
              disabled={page === 1}
              className={`p-2 ${page === 1 ? "text-gray-400" : "text-gray-700 hover:bg-gray-100"
                } rounded-md`}
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
            <button
              onClick={handlePrevPage}
              disabled={page === 1}
              className={`p-2 ${page === 1 ? "text-gray-400" : "text-gray-700 hover:bg-gray-100"
                } rounded-md`}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="flex gap-1">
              {getPageNumbers().map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  className={`px-3 py-1 rounded-md ${pageNum === page
                      ? "bg-[#0eb683] text-white"
                      : "text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            <button
              onClick={handleNextPage}
              disabled={page === totalPages}
              className={`p-2 ${page === totalPages
                  ? "text-gray-400"
                  : "text-gray-700 hover:bg-gray-100"
                } rounded-md`}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              onClick={handleLastPage}
              disabled={page === totalPages}
              className={`p-2 ${page === totalPages
                  ? "text-gray-400"
                  : "text-gray-700 hover:bg-gray-100"
                } rounded-md`}
            >
              <ChevronsRight className="h-4 w-4" />
            </button>
          </div>

          <div className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTable;
